import { Injectable, Renderer2, RendererFactory2, ComponentRef, ViewContainerRef, createComponent, EnvironmentInjector, ApplicationRef } from '@angular/core';
import { TourPopoverComponent } from './tour-popover.component';

export interface PopoverConfig {
  targetElement: HTMLElement;
  content: string;
  title?: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  showNextButton?: boolean;
  onNext?: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class TourPopoverService {
  private renderer: Renderer2;
  private activePopovers: Map<HTMLElement, ComponentRef<TourPopoverComponent>> = new Map();

  constructor(
    private rendererFactory: RendererFactory2,
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /**
   * Show a popover next to target element
   */
  showPopover(config: PopoverConfig): void {
    // Remove existing popover for this element
    this.removePopover(config.targetElement);

    // Create component using modern API
    const componentRef: ComponentRef<TourPopoverComponent> = createComponent(TourPopoverComponent, {
      environmentInjector: this.injector
    });

    // Set component inputs
    componentRef.instance.title = config.title;
    componentRef.instance.content = config.content;
    componentRef.instance.showNextButton = config.showNextButton || false;
    componentRef.instance.onNext = config.onNext;

    // Attach to app
    this.appRef.attachView(componentRef.hostView);

    // Get DOM element from component
    const domElem = componentRef.location.nativeElement as HTMLElement;

    // Add to body
    this.renderer.appendChild(document.body, domElem);

    // Position the popover
    this.positionPopover(domElem, config.targetElement, config.position || 'auto');

    // Store reference
    this.activePopovers.set(config.targetElement, componentRef);

    // Add close handler
    componentRef.instance.onClose = () => {
      this.removePopover(config.targetElement);
    };

    // Reposition on window events
    const repositionHandler = () => {
      this.positionPopover(domElem, config.targetElement, config.position || 'auto');
    };

    window.addEventListener('resize', repositionHandler);
    window.addEventListener('scroll', repositionHandler);

    // Store cleanup function
    (componentRef as any).__cleanupHandler = () => {
      window.removeEventListener('resize', repositionHandler);
      window.removeEventListener('scroll', repositionHandler);
    };
  }

  /**
   * Remove popover from specific element
   */
  removePopover(targetElement: HTMLElement): void {
    const componentRef = this.activePopovers.get(targetElement);
    if (componentRef) {
      // Call cleanup handler if it exists
      if ((componentRef as any).__cleanupHandler) {
        (componentRef as any).__cleanupHandler();
      }

      // Detach and destroy
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
      this.activePopovers.delete(targetElement);
    }
  }

  /**
   * Clear all popovers
   */
  clearAllPopovers(): void {
    this.activePopovers.forEach((componentRef, targetElement) => {
      this.removePopover(targetElement);
    });
    this.activePopovers.clear();
  }

  /**
   * Position popover relative to target element
   */
  private positionPopover(
    popoverElement: HTMLElement,
    targetElement: HTMLElement,
    position: 'top' | 'bottom' | 'left' | 'right' | 'auto'
  ): void {
    const targetRect = targetElement.getBoundingClientRect();
    const popoverRect = popoverElement.getBoundingClientRect();
    const spacing = 12; // Gap between target and popover
    const arrowSize = 8;

    // Calculate best position if auto
    if (position === 'auto') {
      position = this.calculateBestPosition(targetRect, popoverRect);
    }

    let top = 0;
    let left = 0;
    let arrowPosition = '';

    switch (position) {
      case 'top':
        top = targetRect.top - popoverRect.height - spacing;
        left = targetRect.left + (targetRect.width / 2) - (popoverRect.width / 2);
        arrowPosition = 'bottom';
        break;

      case 'bottom':
        top = targetRect.bottom + spacing;
        left = targetRect.left + (targetRect.width / 2) - (popoverRect.width / 2);
        arrowPosition = 'top';
        break;

      case 'left':
        top = targetRect.top + (targetRect.height / 2) - (popoverRect.height / 2);
        left = targetRect.left - popoverRect.width - spacing;
        arrowPosition = 'right';
        break;

      case 'right':
        top = targetRect.top + (targetRect.height / 2) - (popoverRect.height / 2);
        left = targetRect.right + spacing;
        arrowPosition = 'left';
        break;
    }

    // Keep within viewport bounds
    const padding = 16;
    top = Math.max(padding, Math.min(top, window.innerHeight - popoverRect.height - padding));
    left = Math.max(padding, Math.min(left, window.innerWidth - popoverRect.width - padding));

    // Apply position
    this.renderer.setStyle(popoverElement, 'position', 'fixed');
    this.renderer.setStyle(popoverElement, 'top', `${top}px`);
    this.renderer.setStyle(popoverElement, 'left', `${left}px`);
    this.renderer.setStyle(popoverElement, 'z-index', '100001');

    // Set arrow position class
    this.renderer.setAttribute(popoverElement, 'data-arrow-position', arrowPosition);
  }

  /**
   * Calculate best position based on available space
   */
  private calculateBestPosition(
    targetRect: DOMRect,
    popoverRect: DOMRect
  ): 'top' | 'bottom' | 'left' | 'right' {
    const spacing = 12;
    const padding = 16;

    const spaceTop = targetRect.top - padding;
    const spaceBottom = window.innerHeight - targetRect.bottom - padding;
    const spaceLeft = targetRect.left - padding;
    const spaceRight = window.innerWidth - targetRect.right - padding;

    const neededHeight = popoverRect.height + spacing;
    const neededWidth = popoverRect.width + spacing;

    // Prefer bottom, then top, then right, then left
    if (spaceBottom >= neededHeight) {
      return 'bottom';
    } else if (spaceTop >= neededHeight) {
      return 'top';
    } else if (spaceRight >= neededWidth) {
      return 'right';
    } else {
      return 'left';
    }
  }
}
